import React, { useEffect, useState } from 'react';
import { Row, Col, Form, Input, Image, Switch, Tabs, Typography, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useWatch } from 'antd/es/form/Form';
import axios from '../../../config/axiosInstance';
import { useAuth } from '../../../context/AuthContext';

const { Text } = Typography;

const PHASES = [
    { key: 'waiting', label: 'Waiting' },
    { key: 'checking', label: 'Checking' },
    { key: 'printing', label: 'Printing' },
    { key: 'success', label: 'Success' },
    { key: 'denied', label: 'Denied' },
];

const imagePreview = (url) =>
    url ? <Image src={url} alt="preview" style={{ maxWidth: '100%', height: 'auto', marginTop: 8 }} /> : null;

// Image configuration of a single check-in phase: either the client-wide default
// images (switch on) or an own banner/fullscreen background for this phase.
const PhaseImagePane = ({ form, phaseKey, clientDefaults }) => {
    const useDefault = useWatch(['PhaseImages', phaseKey, 'useDefault'], form) !== false;
    const useBackground = useWatch(['PhaseImages', phaseKey, 'isBackgroundImageEnabled'], form) === true;
    const bannerUrl = useWatch(['PhaseImages', phaseKey, 'bannerUrl'], form);
    const backgroundUrl = useWatch(['PhaseImages', phaseKey, 'backgroundUrl'], form);

    // The background counts only with a non-empty URL, same as the delivery gate;
    // otherwise the default banner is shown
    const useDefaultBackground = clientDefaults?.isBackgroundImageEnabled === true
        && !!clientDefaults?.backgroundUrl;
    const defaultUrl = useDefaultBackground
        ? clientDefaults?.backgroundUrl
        : clientDefaults?.bannerUrl;

    return (
        <>
            <Row style={{ marginBottom: 8 }}>
                <Col span={24}>
                    <Form.Item
                        name={['PhaseImages', phaseKey, 'useDefault']}
                        valuePropName="checked"
                        initialValue={true}
                        label={
                            <>
                                Use default images
                                <Tooltip title="Use the default images from the Design page for this phase. Switch off to configure an own banner or background for this phase.">
                                    <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                                </Tooltip>
                            </>
                        }
                    >
                        <Switch />
                    </Form.Item>
                </Col>
            </Row>

            {useDefault ? (
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                        <Text type="secondary">
                            {useDefaultBackground
                                ? 'This phase uses the default fullscreen background from the Design page.'
                                : 'This phase uses the default banner from the Design page.'}
                        </Text>
                    </Col>
                    <Col xs={24} sm={12}>
                        {imagePreview(defaultUrl)}
                    </Col>
                </Row>
            ) : (
                <>
                    <Row style={{ marginBottom: 8 }}>
                        <Col span={24}>
                            <Form.Item
                                name={['PhaseImages', phaseKey, 'isBackgroundImageEnabled']}
                                valuePropName="checked"
                                label={
                                    <>
                                        Fullscreen background
                                        <Tooltip title="Use a fullscreen background image (JPG, PNG, GIF) instead of the banner at the top.">
                                            <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                                        </Tooltip>
                                    </>
                                }
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                            {useBackground ? (
                                <Form.Item name={['PhaseImages', phaseKey, 'backgroundUrl']} label="Background URL">
                                    <Input placeholder="https://.../background.jpg|.png|.gif" />
                                </Form.Item>
                            ) : (
                                <Form.Item name={['PhaseImages', phaseKey, 'bannerUrl']} label="Banner URL">
                                    <Input placeholder="https://.../banner.jpg|.png|.gif" />
                                </Form.Item>
                            )}
                        </Col>
                        <Col xs={24} sm={12}>
                            {imagePreview(useBackground ? backgroundUrl : bannerUrl)}
                        </Col>
                    </Row>
                </>
            )}
        </>
    );
};

const TabImages = ({ form }) => {
    const { hasFeature, isAdmin } = useAuth?.() || { hasFeature: () => false, isAdmin: false };

    // Design customisation (per-phase banner/fullscreen background) is a paid client feature
    const showDesign = hasFeature('deviceConfig.design') || isAdmin;

    const bannerUrl = useWatch('BannerUrl', form);

    // Client default images for the "use default" panes: in the admin list clientID is
    // populated with its defaultImages, otherwise the own client's defaults are fetched.
    const clientFromConfig = useWatch('clientID', form);
    const [ownDefaults, setOwnDefaults] = useState({});

    useEffect(() => {
        if (!showDesign) return;
        if (clientFromConfig && typeof clientFromConfig === 'object') return;

        axios.get('/clients/design/default-images')
            .then(({ data }) => setOwnDefaults(data.defaultImages || {}))
            .catch(() => setOwnDefaults({}));
    }, [showDesign, clientFromConfig]);

    const clientDefaults = (clientFromConfig && typeof clientFromConfig === 'object'
        ? clientFromConfig.defaultImages
        : ownDefaults) || {};

    // Without the design feature the images stay as before: one banner for all phases
    if (!showDesign) {
        return (
            <>
                {/* Description */}
                <Row style={{ marginBottom: 30 }}>
                    <Col span={24}>
                        <Text type="secondary">
                            Define the banner image that will be shown at the top of the check-in screen.
                        </Text>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="BannerUrl"
                            label="Banner URL"
                        >
                            <Input
                                value={form.getFieldValue('BannerUrl')}
                                onChange={(e) => {
                                    form.setFieldsValue({ BannerUrl: e.target.value });
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        {imagePreview(bannerUrl)}
                    </Col>
                </Row>
            </>
        );
    }

    return (
        <>
            {/* Description */}
            <Row style={{ marginBottom: 16 }}>
                <Col span={24}>
                    <Text type="secondary">
                        Define the image per check-in phase: either the default images from the Design
                        page, or an own banner/fullscreen background for the phase.
                    </Text>
                </Col>
            </Row>

            <Tabs
                items={PHASES.map((phase) => ({
                    key: phase.key,
                    label: phase.label,
                    // forceRender: all panes must be mounted so their Form.Items are
                    // registered — otherwise only the visited phases are submitted and
                    // the other phases are lost when PhaseImages is saved
                    forceRender: true,
                    children: <PhaseImagePane form={form} phaseKey={phase.key} clientDefaults={clientDefaults} />,
                }))}
            />
        </>
    );
};

export default TabImages;
